"use client";

import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cva } from "class-variance-authority";
import * as React from "react";
import { BaseTopColors } from "theme";

import { cn } from "../../../lib/utils";

const progressBarContainerVariants = cva("relative h-2 w-full overflow-hidden rounded-full bg-neutral-1", {
	variants: {
		size: {
			4: "h-1",
			8: "h-2",
			12: "h-3",
		},
		defaultVariants: {
			size: 8,
		},
	},
});

const progressBarVariants = cva("size-full flex-1 transition-all", {
	variants: {
		color: {
			primary: "bg-primary-5",
			neutral: "bg-neutral-5",
			red: "bg-red-5",
			green: "bg-green-5",
			yellow: "bg-yellow-5",
		},
	},
	defaultVariants: {
		color: "primary",
	},
});

/**
 * ProgressBar component props interface
 */
export type ProgressBarProps = {
	/** The progress value as a percentage (0-100) */
	color?: BaseTopColors;
	/** Override color with an explicit hex/CSS color value instead of using the palette */
	colorOverride?: string;
	/** The progress value as a percentage (0-100) */
	value: number;
	/** Additional CSS classes to apply to the progress bar container */
	dangerouslySetClassName?: string;
	dangerouslySetBarClassName?: string;
	/** The height of the progress bar in pixels */
	size?: 4 | 8 | 12;
} & Pick<React.AriaAttributes, "aria-label" | "aria-labelledby" | "aria-describedby">;

/**
 * ProgressBar Component
 *
 * A customizable progress bar component built with Radix UI primitives.
 * Displays progress as a visual bar that fills based on the provided value.
 */
const ProgressBar = React.forwardRef<React.ElementRef<typeof ProgressPrimitive.Root>, ProgressBarProps>(
	({ dangerouslySetClassName, dangerouslySetBarClassName, value, color, colorOverride, size, ...props }, ref) => {
		return (
			<ProgressPrimitive.Root
				ref={ref}
				data-slot="progress"
				className={cn(progressBarContainerVariants({ size }), dangerouslySetClassName)}
				{...props}
			>
				<ProgressPrimitive.Indicator
					data-slot="progress-indicator"
					className={cn(
						progressBarVariants({ color: colorOverride ? undefined : color }),
						dangerouslySetBarClassName
					)}
					style={{
						transform: `translateX(-${100 - (value || 0)}%)`,
						...(colorOverride ? { backgroundColor: colorOverride } : {}),
					}}
				/>
			</ProgressPrimitive.Root>
		);
	}
);

ProgressBar.displayName = "ProgressBar";

export { ProgressBar };
